import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import ToDoFilterBar from "../components/todo/ToDoFilterBar";
import ToDoMemberTable from "../components/todo/ToDoMemberTable";
import ToDoEditDialog from "../components/todo/ToDoEditDialog";
import Pagination from "../components/shared/Pagination";

const ITEMS_PER_PAGE = 20;

const ToDoListPanel = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Date filter — default to today only. appliedStart/appliedEnd drives the actual filter;
  // startDate/endDate is what the user is editing. "Terapkan" commits the edit. Presets commit immediately.
  const todayStr = new Date().toLocaleDateString("en-CA");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [appliedStart, setAppliedStart] = useState(todayStr);
  const [appliedEnd, setAppliedEnd] = useState(todayStr);

  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [marketingList, setMarketingList] = useState([]);
  const [websiteList, setWebsiteList] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchMembers();
    fetchMarketingList();
    fetchWebsiteList();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [members, searchTerm, filterType, appliedStart, appliedEnd]);

  const fetchMembers = async () => {
    try {
      const response = await axiosInstance.get("/members");
      setMembers(response.data);
    } catch (error) {
      toast.error("Gagal memuat data member");
    }
  };

  const fetchMarketingList = async () => {
    try {
      const response = await axiosInstance.get("/marketing-list");
      setMarketingList(response.data);
    } catch (error) {
      console.error("Failed to fetch marketing list");
    }
  };

  const fetchWebsiteList = async () => {
    try {
      const response = await axiosInstance.get("/website-list");
      setWebsiteList(response.data);
    } catch (error) {
      console.error("Failed to fetch website list");
    }
  };

  const applyFilters = () => {
    let filtered = members;

    if (filterType !== "all") {
      filtered = filtered.filter((m) => m.member_type === filterType);
    }

    if (appliedStart || appliedEnd) {
      const startTs = appliedStart ? new Date(appliedStart + "T00:00:00").getTime() : -Infinity;
      const endTs = appliedEnd ? new Date(appliedEnd + "T23:59:59").getTime() : Infinity;
      filtered = filtered.filter((m) => {
        if (!m.created_at) return false;
        const ts = new Date(m.created_at).getTime();
        if (Number.isNaN(ts)) return false;
        return ts >= startTs && ts <= endTs;
      });
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.username.toLowerCase().includes(q) ||
          m.kode_db.toLowerCase().includes(q) ||
          m.phone.includes(searchTerm) ||
          m.marketing_name.toLowerCase().includes(q) ||
          m.website.toLowerCase().includes(q)
      );
    }

    setFilteredMembers(filtered);
    setCurrentPage(1);
  };

  const handlePresetDate = (preset) => {
    const today = new Date();
    let s = new Date();
    let e = new Date();
    switch (preset) {
      case "today":
        s = e = new Date();
        break;
      case "yesterday":
        s = e = new Date(today.getTime() - 86400000);
        break;
      case "this_week": {
        const dow = today.getDay();
        const offset = dow === 0 ? -6 : 1 - dow;
        s = new Date(today);
        s.setDate(today.getDate() + offset);
        e = new Date(today);
        break;
      }
      case "this_month":
        s = new Date(today.getFullYear(), today.getMonth(), 1);
        e = new Date(today);
        break;
      default:
        break;
    }
    const fmt = (d) => d.toLocaleDateString("en-CA");
    const ss = fmt(s);
    const ee = fmt(e);
    setStartDate(ss);
    setEndDate(ee);
    setAppliedStart(ss);
    setAppliedEnd(ee);
  };

  const applyDateFilter = () => {
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
  };

  const resetDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStart("");
    setAppliedEnd("");
  };

  const openEditDialog = (member) => {
    setSelectedMember(member);
    setEditForm({
      marketing_name: member.marketing_name,
      website: member.website,
      username: member.username,
      kode_db: member.kode_db,
      phone: member.phone,
      member_type: member.member_type,
      nominal_depo: member.nominal_depo || "",
      notes: member.notes || "",
    });
    setShowEditDialog(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    setLoading(true);
    try {
      const payload = {
        ...editForm,
        ...(editForm.member_type === "ndp" ? { nominal_depo: parseFloat(editForm.nominal_depo) } : {}),
      };
      await axiosInstance.put("/members/" + selectedMember.id, payload);
      toast.success("Member berhasil diupdate");
      setShowEditDialog(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengupdate member");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId, memberUsername) => {
    if (!window.confirm(`Yakin ingin menghapus member "${memberUsername}"? Data yang dihapus tidak dapat dikembalikan.`)) {
      return;
    }
    try {
      await axiosInstance.delete("/members/" + memberId);
      toast.success("Member berhasil dihapus");
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menghapus member");
    }
  };

  const toggleSelectMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleSelectAll = () => {
    const ndpMembers = filteredMembers.filter((m) => m.member_type === "ndp");
    if (selectedMembers.length === ndpMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(ndpMembers.map((m) => m.id));
    }
  };

  const handleSubmitMembers = async () => {
    if (selectedMembers.length === 0) {
      toast.error("Pilih minimal 1 member untuk disubmit");
      return;
    }
    if (!window.confirm(`Submit ${selectedMembers.length} member ke Member Report?`)) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post("/members/submit", { member_ids: selectedMembers });
      toast.success(response.data.message);
      setSelectedMembers([]);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal submit member");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="todo-list-panel">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/dashboard")}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white hover:bg-red-900/20"
                data-testid="back-button"
              >
                <ArrowLeft className="mr-2" size={18} />
                Kembali
              </Button>
              <h1 className="text-2xl font-bold text-red-50">Submit Member</h1>
            </div>
            <div className="flex gap-2">
              {selectedMembers.length > 0 && (
                <Button
                  onClick={handleSubmitMembers}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  disabled={loading}
                  data-testid="submit-members-button"
                >
                  <Send className="mr-2" size={16} />
                  Submit ({selectedMembers.length})
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
          <CardHeader>
            <CardTitle className="text-red-50">Semua Data Member</CardTitle>
            <CardDescription className="text-gray-400">
              Lihat, edit, dan tambahkan catatan untuk semua member
            </CardDescription>
            <ToDoFilterBar
              members={members}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterType={filterType}
              setFilterType={setFilterType}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              onPreset={handlePresetDate}
              onApply={applyDateFilter}
              onReset={resetDateFilter}
            />
          </CardHeader>
          <CardContent>
            {filteredMembers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                {searchTerm || filterType !== "all" ? "Tidak ada member yang cocok" : "Belum ada member"}
              </div>
            ) : (
              <div className="rounded-lg border border-red-900/30 overflow-hidden">
                <ToDoMemberTable
                  paginatedMembers={paginatedMembers}
                  filteredMembers={filteredMembers}
                  selectedMembers={selectedMembers}
                  toggleSelectMember={toggleSelectMember}
                  toggleSelectAll={toggleSelectAll}
                  onEdit={openEditDialog}
                  onDelete={handleDeleteMember}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <ToDoEditDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          editForm={editForm}
          setEditForm={setEditForm}
          marketingList={marketingList}
          websiteList={websiteList}
          onSubmit={handleUpdateMember}
          loading={loading}
        />
      </main>
    </div>
  );
};

export default ToDoListPanel;
